import { Q } from "@nozbe/watermelondb";
import { EntityNode } from "@towersystems/roam-common/lib/generated-types";
import { useCallback, useEffect, useState } from "react";
import {
  PageQueryOptions,
  SyncProgressEvent,
  SyncProgressStatus,
} from "../setup/page-query";
import { SyncState } from "./constants";
import { database } from "./database";
import { BaseModel } from "./model/Base";
import FetchMeta from "./model/FetchMeta";
import { SyncMeta } from "./model/SyncMeta";

export const useDatabase = () => {
  return database;
};

export const useFetchAndSyncDatabaseEntities = () => {
  const database = useDatabase();

  const fetchEntity = useCallback(
    async <
      T extends EntityNode & {
        createdAt: Date;
        updatedAt: Date;
        deletedAt?: Date;
      },
      J extends BaseModel
    >(
      key: string,
      fetchEntities: (options?: PageQueryOptions) => Promise<T[]>,
      prepareEntity: (record: J, fetchedEntity: T) => void,
      progressCallback?: PageQueryOptions["progressCallback"]
    ) => {
      const fetchTime = new Date();

      let maybeExistingSyncMeta: FetchMeta | undefined;
      try {
        maybeExistingSyncMeta = await database
          .get<FetchMeta>("fetch_meta")
          .find(key);
      } catch (e) {
        console.log(e);
      }
      try {
        const lastFetched = maybeExistingSyncMeta?.lastFetch;

        const entities = await fetchEntities(
          {
            after: lastFetched ?? undefined,
            progressCallback,
          } ?? undefined
        );

        const batchSize = 1000; // Set the batch size to 1000 items
        const totalItems = entities.length;

        for (let i = 0; i < totalItems; i += batchSize) {
          const batchActions: BaseModel[] = [];

          const batchItems = entities.slice(i, i + batchSize);

          for (const entity of batchItems) {
            let maybeExisting;

            try {
              maybeExisting = await database.get<J>(key).find(entity.id);
            } catch (err) {}

            if (!maybeExisting) {
              try {
                const p = database.get<J>(key).prepareCreate((newEntity) => {
                  newEntity._raw.id = entity.id;
                  newEntity.createdAt = new Date(entity.createdAt);
                  newEntity.deletedAt = entity.deletedAt
                    ? new Date(entity.deletedAt)
                    : null;
                  newEntity.updatedAt = new Date(entity.updatedAt);
                  prepareEntity(newEntity, entity);
                });

                batchActions.push(p);
              } catch (e) {
                console.log(`FAILED TO CREATE [${key}]`, e, entity);
              }
            } else {
              try {
                try {
                  const p = maybeExisting.prepareUpdate((newEntity) => {
                    newEntity.createdAt = new Date(entity.createdAt);
                    newEntity.deletedAt = entity.deletedAt
                      ? new Date(entity.deletedAt)
                      : null;
                    newEntity.updatedAt = new Date(entity.updatedAt);
                    prepareEntity(newEntity, entity);
                  });

                  batchActions.push(p);
                } catch (err: any) {
                  if (err?.message && err.message.includes("not found")) {
                    const p = database
                      .get<J>(key)
                      .prepareCreate((newEntity) => {
                        newEntity._raw.id = entity.id;
                        newEntity.createdAt = new Date(entity.createdAt);
                        newEntity.deletedAt = entity.deletedAt
                          ? new Date(entity.deletedAt)
                          : null;
                        newEntity.updatedAt = new Date(entity.updatedAt);
                        prepareEntity(newEntity, entity);
                      });

                    batchActions.push(p);
                  } else {
                    throw err;
                  }
                }
              } catch (e) {
                console.log(`FAILED TO UPDATE [${key}]`, e, entity);
              }
            }
          }

          await progressCallback?.({
            status: SyncProgressStatus.SYNCING,
          });
          await database.write(async () => {
            await database.batch(batchActions);
          });
        }

        await database.write(async () => {
          if (!maybeExistingSyncMeta) {
            await database.get<FetchMeta>("fetch_meta").create((newMeta) => {
              newMeta._raw.id = key;
              newMeta.lastFetch = fetchTime;
              newMeta.initialSyncOccoured = true;
            });
          } else {
            const fetchMeta = await database
              .get<FetchMeta>("fetch_meta")
              .find(key);
            await fetchMeta.update(() => {
              fetchMeta.lastFetch = fetchTime;
              fetchMeta.initialSyncOccoured = true;
            });
          }
        });

        await progressCallback?.({
          status: SyncProgressStatus.COMPLETE,
        });
      } catch (err) {
        console.log(err);
        await progressCallback?.({
          status: SyncProgressStatus.ERROR,
        });
      }
    },
    []
  );

  return {
    fetchEntity,
  };
};

export const useGetEntity = <T extends BaseModel>(key: string, id: string) => {
  const [entity, setEntity] = useState<T | null>(null);
  const database = useDatabase();
  useEffect(() => {
    database
      .get<T>(key)
      .find(id)
      .catch(() => null)
      .then(setEntity);
  }, [database, key, id]);
  return entity;
};

export function useSyncableEntities() {
  const database = useDatabase();

  async function findSyncableEntities<T extends SyncMeta>(table: string) {
    const collection = database.collections.get<T>(table);

    const entities = await collection
      .query(
        Q.where("sync_state", SyncState.UPLOAD),
        Q.and(
          Q.or(
            Q.where("last_sync_attempt", Q.eq(null)),
            Q.where("last_sync_attempt", Q.notEq(null))
          )
        )
      )
      .fetch();

    return entities;
  }

  return {
    findSyncableEntities,
  };
}
